import React from 'react';
import { Helmet } from 'react-helmet-async';
import { siteConfig } from '../data/site-content';
import { Link } from 'react-router-dom';

const Hypnose = () => {
  return (
    <>
      <Helmet>
        <title>Hypnose & Hypnoterapi i Valby – {siteConfig.name}</title>
        <meta name="description" content="Oplev professionel, klinisk hypnose og hypnoterapi i Valby hos Core Hypnose. Skab ro, fokus og nye reaktionsmønstre med Lars Nissen Corell – Psykoterapeut MPF." />
      </Helmet>

      {/* 1. Hero Section */}
      <section className="py-20 bg-[#FAF8F5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <div className="flex items-center gap-2">
                <span className="h-[1px] w-8 bg-[#8C2F12]"></span>
                <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Behandlingsmetode</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-serif text-[#123F13] leading-tight font-medium">
                Hypnose & hypnoterapi
              </h1>
              <p className="text-lg font-medium text-[#8C2F12] font-sans">
                Skab ro, fokus og nye reaktionsmønstre
              </p>
              <p className="text-base md:text-lg text-[#3B352F]/80 leading-relaxed">
                Hypnose er en rolig og fokuseret tilstand, hvor opmærksomheden samles, og kroppen lettere kan falde til ro. Hos Core Hypnose bruges hypnose som en terapeutisk metode — ikke til at fjerne din kontrol, men til at hjælpe dig med at arbejde mere målrettet med tanker, uro, vaner og kropslige reaktioner.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <Link to="/bestil-tid" className="bg-[#123F13] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#123F13]/90 transition-all flex items-center gap-2 shadow-sm">
                  Book første session <i className="fa-light fa-arrow-right" />
                </Link>
                <a href="tel:+4527204671" className="border-2 border-[#123F13] text-[#123F13] px-8 py-4 rounded-lg font-bold hover:bg-[#123F13] hover:text-white transition-all flex items-center gap-2">
                  <i className="fa-light fa-phone" /> Ring 27 20 46 71
                </a>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="relative">
                <div className="absolute inset-0 bg-[#123F13]/5 rounded-2xl transform translate-x-3 translate-y-3 -z-10"></div>
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/3af30f33-f8f6-4ae9-b753-0f99b021f3b2.png" 
                  alt="Hypnose og Hypnoterapi hos Core Hypnose" 
                  className="w-full h-auto object-cover rounded-2xl shadow-md border border-[#E8DDCE]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Klientudtalelse / Testimonial */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-[#123F13] text-white p-8 md:p-12 rounded-2xl relative overflow-hidden shadow-md">
            <div className="absolute top-6 right-8 text-white/10 text-7xl font-serif select-none pointer-events-none">
              <i className="fa-solid fa-quote-right" />
            </div>
            <div className="relative z-10 max-w-2xl space-y-4">
              <h3 className="text-xl md:text-2xl font-serif italic leading-snug">
                “Jeg kan skabe dyb ro i kroppen på under 1 minut”
              </h3>
              <p className="text-white/80 leading-relaxed text-sm md:text-base">
                ...med en simpel øvelse Lars har lært mig, og det får mit system helt ned. Det er super givende og har givet mig fantastiske redskaber.
              </p>
              <div className="pt-2 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <p className="font-bold text-sm md:text-base">— Jacko Hansen, tidligere klient</p>
                <p className="text-white/60 text-xs italic">Fik effektive redskaber til at berolige nervesystemet.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Hvad er hypnose egentlig? */}
      <section className="py-16 bg-[#FAF8F5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] text-[#8C2F12] uppercase block mb-3">Faglig forklaring</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13]">Hvad er hypnose egentlig?</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
            <div className="md:col-span-7 bg-white p-8 rounded-2xl border border-[#EAE5DC] shadow-sm flex flex-col justify-between">
              <div className="space-y-4 text-[#3B352F]/80 leading-relaxed text-sm md:text-base">
                <p>
                  Hypnose er ikke søvn, magi eller kontroltab. Det er en tilstand af fokuseret opmærksomhed, hvor du stadig er vågen, til stede og kan høre, hvad der bliver sagt.
                </p>
                <p>
                  Mange oplever hypnose som en rolig og koncentreret tilstand, hvor kroppen falder mere til ro, og hvor det bliver lettere at arbejde med indre billeder, kropslige fornemmelser, vaner eller automatiske reaktioner.
                </p>
                <p>
                  I behandlingen bruges hypnose som et redskab. Det betyder, at hypnosen ikke står alene, men indgår i et terapeutisk arbejde med det, du ønsker at ændre.
                </p>
              </div>
            </div>

            <div className="md:col-span-5 bg-[#F4EFE7]/40 p-8 rounded-2xl border border-[#EAE5DC] flex flex-col justify-center space-y-4">
              <div className="w-10 h-10 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center text-lg">
                <i className="fa-light fa-circle-check" />
              </div>
              <h3 className="text-lg font-bold text-[#123F13] font-sans">Kort sagt:</h3>
              <p className="text-sm md:text-base text-[#3B352F]/80 leading-relaxed">
                Du mister ikke kontrollen. Du får hjælp til at samle opmærksomheden og arbejde mere direkte med det, der holder problemet i gang.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Infografik: Hypnose uden mystik */}
      <section className="py-16 bg-white border-t border-b border-[#EAE5DC]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-[1px] w-8 bg-[#EAE5DC]"></div>
              <span className="text-xs font-bold tracking-[0.2em] text-[#8C2F12] uppercase">Sammenligning</span>
              <div className="h-[1px] w-8 bg-[#EAE5DC]"></div>
            </div>
            <h2 className="text-3xl font-serif text-[#123F13]">Hypnose uden mystik</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Kolonne 1: Ikke (Terracotta) */}
            <div className="rounded-2xl border border-[#EADBD5] p-6 md:p-8 bg-[#FAF8F5]/50 flex flex-col gap-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#F7EBE8] text-[#8C2F12] flex items-center justify-center text-sm">
                  <i className="fa-light fa-xmark" />
                </div>
                <h3 className="text-2xl font-serif text-[#8C2F12]">Ikke</h3>
              </div>

              <div className="flex flex-col gap-3">
                {[
                  { label: "Søvn", icon: "fa-moon" },
                  { label: "Kontroltab", icon: "fa-lock" },
                  { label: "Hokus pokus", icon: "fa-wand-magic-sparkles" },
                  { label: "At nogen overtager styringen", icon: "fa-user-shield" }
                ].map((item, idx) => (
                  <div key={idx} className="bg-white rounded-xl p-4 flex items-center gap-4 border border-[#F1EDE8] shadow-2xs">
                    <div className="w-10 h-10 rounded-full bg-[#F7EBE8] text-[#8C2F12] flex items-center justify-center shrink-0 text-base">
                      <i className={`fa-light ${item.icon}`} />
                    </div>
                    <span className="font-bold text-sm md:text-base text-[#1C2826]">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Kolonne 2: Men (Mørkegrøn) */}
            <div className="rounded-2xl border border-[#DFEAE5] p-6 md:p-8 bg-[#FAF8F5]/50 flex flex-col gap-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#EEF5F2] text-[#123F13] flex items-center justify-center text-sm">
                  <i className="fa-light fa-check" />
                </div>
                <h3 className="text-2xl font-serif text-[#123F13]">Men</h3>
              </div>

              <div className="flex flex-col gap-3">
                {[
                  { label: "Fokuseret opmærksomhed", icon: "fa-eye" },
                  { label: "Rolig guidning", icon: "fa-compass" },
                  { label: "Terapeutiske forslag", icon: "fa-comment-dots" },
                  { label: "Nye måder at reagere på", icon: "fa-route" }
                ].map((item, idx) => (
                  <div key={idx} className="bg-white rounded-xl p-4 flex items-center gap-4 border border-[#F1EDE8] shadow-2xs">
                    <div className="w-10 h-10 rounded-full bg-[#EEF5F2] text-[#123F13] flex items-center justify-center shrink-0 text-base">
                      <i className={`fa-light ${item.icon}`} />
                    </div>
                    <span className="font-bold text-sm md:text-base text-[#1C2826]">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bundtekst i hvid boks med mørkegrøn venstre-kant accent */}
          <div className="bg-white border border-[#F1EDE8] border-l-4 border-l-[#123F13] rounded-2xl p-6 flex gap-4 items-start shadow-xs">
            <div className="w-10 h-10 rounded-full bg-[#EEF5F2] text-[#123F13] flex items-center justify-center shrink-0 text-base">
              <i className="fa-light fa-shield-check" />
            </div>
            <p className="text-sm md:text-base text-[#3B352F]/80 leading-relaxed">
              <strong className="text-[#123F13] font-bold">Du er stadig vågen, til stede og i kontrol under hypnose.</strong> Målet er ikke at fjerne dig fra dig selv, men at skabe ro, fokus og bedre mulighed for at arbejde med det, du ønsker at ændre.
            </p>
          </div>
        </div>
      </section>

      {/* 5. Mister man kontrollen? */}
      <section className="py-16 bg-[#FAF8F5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-5">
              <div className="relative">
                <div className="absolute inset-0 bg-[#8C2F12]/5 rounded-2xl transform translate-x-2 translate-y-2 -z-10"></div>
                <div className="bg-white p-8 rounded-2xl border border-[#EAE5DC] text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-[#F7EBE8] text-[#8C2F12] flex items-center justify-center mx-auto text-2xl">
                    <i className="fa-light fa-user-shield" />
                  </div>
                  <h3 className="text-xl font-serif text-[#123F13] font-medium">Fuld kontrol</h3>
                  <p className="text-xs text-[#3B352F]/60 uppercase tracking-widest font-bold">Du kan altid sige fra</p>
                </div>
              </div>
            </div>
            <div className="md:col-span-7 space-y-6">
              <h2 className="text-3xl font-serif text-[#123F13]">Mister man kontrollen?</h2>
              <div className="space-y-4 text-[#3B352F]/80 leading-relaxed text-sm md:text-base">
                <p className="font-bold text-[#123F13]">
                  Nej. Du er vågen og kan sige fra under hele sessionen.
                </p>
                <p>
                  Hypnose handler ikke om, at en anden overtager kontrollen. Det handler om, at du guides ind i en mere fokuseret og rolig tilstand, hvor det bliver lettere at arbejde med det, der normalt sker automatisk — for eksempel uro, spændinger, vaner, undgåelse eller tankemønstre.
                </p>
                <p>
                  Du kan sammenligne det med at være dybt optaget af en bog, en film eller en tanke. Opmærksomheden bliver mere samlet, men du er stadig dig selv.
                </p>
              </div>
            </div>
          </div>

          {/* 3. Indsæt kort fremhævet tekstboks (Broen mellem forklaring og forskning) */}
          <div className="mt-16 bg-[#F4EFE7]/60 border border-[#EAE5DC] rounded-2xl p-6 md:p-8 shadow-2xs flex flex-col sm:flex-row gap-6 items-start">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#EEF5F2] text-[#123F13] flex items-center justify-center text-lg">
              <i className="fa-light fa-circle-info" />
            </div>
            <div className="space-y-2">
              <h4 className="text-lg font-bold text-[#123F13] font-sans">Psykologisk metode</h4>
              <p className="text-sm md:text-base text-[#3B352F]/80 leading-relaxed">
                Hypnose er ikke mystik. Det er en psykologisk metode, der er undersøgt i forskning, og hvor fokuseret opmærksomhed, rolig guidning og terapeutiske forslag kan hjælpe med at arbejde mere målrettet med tanker, følelser, kropslige reaktioner og vaner.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Ny sektion: Hvad viser forskningen om hypnose? */}
      <section className="py-20 bg-white border-t border-[#EAE5DC]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-xs font-bold tracking-[0.2em] text-[#8C2F12] uppercase block">Evidens & Videnskab</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13]">Hvad viser forskningen om hypnose?</h2>
            <p className="text-base md:text-lg text-[#3B352F]/80 leading-relaxed">
              Forskningen i hypnose er ikke sort-hvid. Der findes områder, hvor hypnose er undersøgt grundigt, og andre områder hvor der stadig mangler mere viden.
            </p>
            <p className="text-sm md:text-base text-[#3B352F]/70 leading-relaxed">
              Det mest solide billede er, at hypnose kan være relevant som supplement i behandling — især når der arbejdes med smerte, angst, kropslig uro, medicinske procedurer og mental belastning.
            </p>
          </div>

          {/* 4 Kort med faglige pointer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-[#FAF8F5] border border-[#EAE5DC] rounded-2xl p-6 md:p-8 hover:shadow-xs transition-all space-y-4">
              <div className="w-10 h-10 rounded-full bg-[#EEF5F2] text-[#123F13] flex items-center justify-center text-base">
                <i className="fa-light fa-heart-pulse" />
              </div>
              <h3 className="text-lg font-bold text-[#123F13] font-sans">Hypnose kan støtte mental og kropslig ro</h3>
              <p className="text-sm md:text-base text-[#3B352F]/70 leading-relaxed">
                Forskning peger på, at hypnose kan have positiv effekt på både mentale og kropslige behandlingsresultater.
              </p>
            </div>

            <div className="bg-[#FAF8F5] border border-[#EAE5DC] rounded-2xl p-6 md:p-8 hover:shadow-xs transition-all space-y-4">
              <div className="w-10 h-10 rounded-full bg-[#EEF5F2] text-[#123F13] flex items-center justify-center text-base">
                <i className="fa-light fa-stethoscope" />
              </div>
              <h3 className="text-lg font-bold text-[#123F13] font-sans">Undersøgt ved smerte, angst og procedurer</h3>
              <p className="text-sm md:text-base text-[#3B352F]/70 leading-relaxed">
                Der findes forskning, der peger på, at hypnose kan reducere smerte, følelsesmæssig belastning og ubehag i bestemte behandlingssammenhænge.
              </p>
            </div>

            <div className="bg-[#FAF8F5] border border-[#EAE5DC] rounded-2xl p-6 md:p-8 hover:shadow-xs transition-all space-y-4">
              <div className="w-10 h-10 rounded-full bg-[#EEF5F2] text-[#123F13] flex items-center justify-center text-base">
                <i className="fa-light fa-puzzle-piece" />
              </div>
              <h3 className="text-lg font-bold text-[#123F13] font-sans">Hypnose kan være relevant som supplement</h3>
              <p className="text-sm md:text-base text-[#3B352F]/70 leading-relaxed">
                Hypnose bør ikke fremstilles som en mirakelmetode. Den giver mest mening, når den bruges seriøst, målrettet og som del af en klar terapeutisk retning.
              </p>
            </div>

            <div className="bg-[#FAF8F5] border border-[#EAE5DC] rounded-2xl p-6 md:p-8 hover:shadow-xs transition-all space-y-4">
              <div className="w-10 h-10 rounded-full bg-[#EEF5F2] text-[#123F13] flex items-center justify-center text-base">
                <i className="fa-light fa-chart-line" />
              </div>
              <h3 className="text-lg font-bold text-[#123F13] font-sans">Resultaterne varierer fra område til område</h3>
              <p className="text-sm md:text-base text-[#3B352F]/70 leading-relaxed">
                Det er vigtigt, at siden ikke lover for meget. Formuleringen bør være “kan være hjælpsomt” frem for “virker mod alt”.
              </p>
            </div>
          </div>

          {/* Afsluttende tekst */}
          <div className="bg-[#FAF8F5] border border-[#EAE5DC] rounded-2xl p-6 md:p-8 text-center max-w-3xl mx-auto mb-12">
            <p className="text-sm md:text-base text-[#3B352F]/80 leading-relaxed">
              Hypnose er ikke hokus pokus. Det er heller ikke en garanti. Hos Core Hypnose bruges hypnose ikke alene, men altid sammen med metakognitiv terapi, så arbejdet både handler om ro, fokuseret opmærksomhed og nye måder at forholde sig til tanker og reaktioner på.
            </p>
          </div>

          {/* Kilder / forskningsreferencer */}
          <div className="border-t border-[#EAE5DC] pt-8">
            <h4 className="text-xs font-bold uppercase tracking-widest text-[#8C2F12] mb-4 flex items-center gap-2">
              <i className="fa-light fa-book-open" /> Udvalgte forskningsreferencer og kilder
            </h4>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-[#3B352F]/70">
              <li>
                <a 
                  href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10807512/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-[#8C2F12] transition-colors flex items-start gap-2"
                >
                  <i className="fa-light fa-arrow-up-right-from-square mt-0.5 shrink-0" />
                  <span>
                    <strong>Meta-analytic evidence on the efficacy of hypnosis:</strong> PMC10807512 (Mental and somatic health outcomes).
                  </span>
                </a>
              </li>
              <li>
                <a 
                  href="https://pubmed.ncbi.nlm.nih.gov/39388785/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-[#8C2F12] transition-colors flex items-start gap-2"
                >
                  <i className="fa-light fa-arrow-up-right-from-square mt-0.5 shrink-0" />
                  <span>
                    <strong>Systematic review of randomized clinical trials:</strong> PubMed 39388785 (Hypnotherapy and depression).
                  </span>
                </a>
              </li>
              <li>
                <a 
                  href="https://www.sciencedirect.com/science/article/abs/pii/S1744388124000860" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-[#8C2F12] transition-colors flex items-start gap-2"
                >
                  <i className="fa-light fa-arrow-up-right-from-square mt-0.5 shrink-0" />
                  <span>
                    <strong>Hypnosis for depression:</strong> ScienceDirect (A systematic review and meta-analysis).
                  </span>
                </a>
              </li>
              <li>
                <a 
                  href="https://www.mdpi.com/2673-5318/7/1/16" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-[#8C2F12] transition-colors flex items-start gap-2"
                >
                  <i className="fa-light fa-arrow-up-right-from-square mt-0.5 shrink-0" />
                  <span>
                    <strong>Ericksonian Hypnotherapy:</strong> MDPI 2024 (Systematic review and meta-analysis).
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 7. Hvad kan hypnose hjælpe med? */}
      <section className="py-20 bg-[#123F13] text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <span className="text-xs font-bold tracking-[0.2em] text-[#E8DDCE] uppercase block">Anvendelsesområder</span>
            <h2 className="text-3xl md:text-4xl font-serif">Hvad kan hypnose hjælpe med?</h2>
            <p className="text-white/80 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              Hypnose kan være relevant, når kroppen, tankerne eller vanerne reagerer automatisk — også selvom du godt ved rationelt, hvad du gerne vil gøre anderledes.
            </p>
            <p className="text-white/70 max-w-2xl mx-auto text-xs md:text-sm">
              Hos Core Hypnose bruges hypnose især som støtte til at skabe ro, fokus og nye reaktionsmønstre.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "fa-brain",
                title: "Angst & uro",
                desc: "Find ro i nervesystemet, dæmp kropslige reaktioner og bryd de automatiske mønstre, der holder angsten ved lige."
              },
              {
                icon: "fa-ban",
                title: "Vaner & afhængighed",
                desc: "Få konkret hjælp til at bryde uhensigtsmæssige vaner som f.eks. rygning eller overspisning ved at arbejde med de automatiske impulser."
              },
              {
                icon: "fa-heart",
                title: "Selvværd & selvtillid",
                desc: "Styrk din indre dialog, dæmp selvkritik og opbyg en mere stabil og rolig tro på egne evner."
              },
              {
                icon: "fa-spider",
                title: "Fobier",
                desc: "Målrettet hjælp til at dæmpe den intense, automatiske frygtreaktion ved f.eks. højder, flyvning, nåle eller edderkopper."
              },
              {
                icon: "fa-wind",
                title: "Stresshåndtering",
                desc: "Giv kroppen og nervesystemet mulighed for at restituere dybt, og lær at dæmpe den konstante indre alarmtilstand."
              },
              {
                icon: "fa-bullseye",
                title: "Præstation og fokus",
                desc: "Bliv bedre til at samle din opmærksomhed, bevare roen og præstere under pres, f.eks. til eksamener eller vigtige begivenheder."
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-white/10 p-8 rounded-2xl border border-white/10 hover:bg-white/15 transition-all space-y-4">
                <div className="w-12 h-12 rounded-full bg-white/10 text-[#E8DDCE] flex items-center justify-center text-xl">
                  <i className={`fa-light ${item.icon}`} />
                </div>
                <h3 className="text-xl font-serif font-medium">{item.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Hvordan foregår hypnosebehandling? */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 space-y-3">
            <span className="text-xs font-bold tracking-[0.2em] text-[#8C2F12] uppercase block">Processen</span>
            <h2 className="text-3xl font-serif text-[#123F13]">Hvordan foregår hypnosebehandling?</h2>
            <p className="text-sm md:text-base text-[#3B352F]/70">
              Et trygt, gennemskueligt og professionelt forløb uden overraskelser. For autistiske personer tilpasses hypnosen altid, så den fungerer som en rolig, forudsigelig guidning uden pres. Læs mere om mit <Link to="/autismevenlig-terapi" className="text-[#8C2F12] font-semibold hover:underline">autismevenlige forløb</Link>.
            </p>
          </div>

          <div className="space-y-12 relative before:absolute before:inset-y-0 before:left-6 before:w-[1px] before:bg-[#EAE5DC] before:-z-10">
            {[
              {
                num: "1",
                title: "Samtale først",
                desc: "Sessionen starter med en rolig samtale om det, du ønsker hjælp til. Du behøver ikke have alle svarene på forhånd."
              },
              {
                num: "2",
                title: "Fokus og forberedelse",
                desc: "Lars forklarer, hvordan hypnosen foregår, så du ved, hvad du kan forvente. Der er ingen overraskelser og ingen kontroltab."
              },
              {
                num: "3",
                title: "Guidet hypnose",
                desc: "Du guides ind i en rolig og fokuseret tilstand, hvor behandlingen kan arbejde med tanker, kropslige reaktioner, vaner eller indre mønstre."
              },
              {
                num: "4",
                title: "Redskaber til hverdagen",
                desc: "Efter hypnosen samles der op. Du får konkrete pointer eller øvelser, du kan bruge efter sessionen."
              }
            ].map((step, idx) => (
              <div key={idx} className="flex gap-6 items-start relative z-10">
                <div className="w-12 h-12 bg-[#FAF8F5] border border-[#EAE5DC] text-[#8C2F12] rounded-full flex items-center justify-center font-bold shrink-0 shadow-2xs">
                  {step.num}
                </div>
                <div className="bg-[#FAF8F5]/40 border border-[#EAE5DC]/60 p-6 rounded-2xl flex-1 space-y-2">
                  <h3 className="text-lg font-bold text-[#123F13] font-sans">{step.title}</h3>
                  <p className="text-sm md:text-base text-[#3B352F]/70 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. Afsluttende CTA / book første session */}
      <section className="py-20 bg-[#FAF8F5] border-t border-[#EAE5DC]">
        <div className="max-w-6xl mx-auto px-6 text-center space-y-8">
          <div className="w-16 h-16 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center mx-auto text-2xl">
            <i className="fa-light fa-calendar-check" />
          </div>
          <div className="space-y-4">
            <h2 className="text-3xl font-serif text-[#123F13]">Klar til at skabe forandring?</h2>
            <p className="text-base md:text-lg text-[#3B352F]/70 max-w-2xl mx-auto leading-relaxed">
              Tag det første skridt mod en lettere hverdag. Book en session i dag og mærk forskellen.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-2">
            <Link to="/bestil-tid" className="bg-[#123F13] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#123F13]/90 transition-all flex items-center justify-center gap-2 shadow-sm">
              Book din første session (375 kr) <i className="fa-light fa-arrow-right" />
            </Link>
          </div>
          <div className="space-y-1">
            <p className="text-xs md:text-sm text-[#3B352F]/50 italic">
              Ingen binding – du mærker efter første session, om det er det rette for dig.
            </p>
            <p className="text-xs text-[#3B352F]/40">
              Sessionerne afholdes i rolige omgivelser i Valby.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hypnose;
